import { ChevronRight, Stethoscope, Headphones, Wind, ExternalLink, Activity, Scan, Ear, FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const services = [
  { title: "Videonasofibroscopia", description: "Exame endoscópico para avaliação detalhada do nariz, rinofaringe e vias aéreas superiores.", image: "/images/consulta.png", link: "/servicos/videonasofibroscopia", icon: Scan },
  { title: "Videolaringoscopia", description: "Avaliação da laringe e cordas vocais para investigação de rouquidão, alterações da voz e lesões.", image: "/images/consulta.png", link: "/servicos/videolaringoscopia", icon: Activity },
  { title: "Videoestroboscopia", description: "Exame especializado para análise dinâmica da vibração das cordas vocais.", image: "/images/consulta.png", link: "/servicos/videoestroboscopia", icon: Activity },
  { title: "Tomografias", description: "Exames de imagem para avaliação dos seios da face, ouvido e estruturas relacionadas.", image: "/images/consulta.png", link: "/servicos/tomografias", icon: Scan },
  { title: "Raio X", description: "Exames radiológicos complementares para investigação diagnóstica em otorrinolaringologia.", image: "/images/consulta.png", link: "/servicos/raio-x", icon: Scan },
  { title: "Audiometria e BERA", description: "Avaliação auditiva completa, incluindo exames objetivos para diagnóstico da audição.", image: "/images/medico.png", link: "/servicos/audiometria-e-bera", icon: Ear },
  { title: "Exames de Otoneurologia", description: "Investigação de tontura, vertigem, desequilíbrio e alterações do labirinto.", image: "/images/medico.png", link: "/servicos/exames-de-otoneurologia", icon: Headphones },
  { title: "Exames para Labirintopatias", description: "Avaliação direcionada para doenças do labirinto e sintomas vestibulares.", image: "/images/medico.png", link: "/servicos/exames-para-labirintopatias", icon: Headphones },
  { title: "Imunoterapia e Testes Alérgicos", description: "Diagnóstico e tratamento de alergias respiratórias com acompanhamento especializado.", image: "/images/online.png", link: "/servicos/imunoterapia-testes-alergicos", icon: FlaskConical },
];

const Servicos = () => {
  const links = services.map(({ title, link }) => ({ title, link }));

  return (
    <div data-testid="servicos-page">
      <section className="bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground pt-28 md:pt-32 pb-12 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-30" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent blob" />
        <div className="container-pad relative">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-accent" />
            <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">NOSSOS SERVIÇOS</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-light leading-tight mb-3 text-balance">Serviços médicos especializados</h1>
          <p className="max-w-2xl text-primary-foreground/75 text-sm md:text-base">Exames, diagnóstico e tratamentos em otorrinolaringologia com tecnologia e atendimento humanizado.</p>
        </div>
      </section>

      <section className="container-pad py-14 md:py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.a
                key={svc.title}
                href={svc.link}
                target={svc.link?.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 6) * 0.06 }}
                className="group bg-white rounded-3xl overflow-hidden border border-border/60 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="relative aspect-[5/4] overflow-hidden bg-gradient-to-br from-secondary to-white p-6 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center group-hover:scale-105 transition">
                    <Icon className="w-9 h-9" />
                  </div>
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-[10px] tracking-widest uppercase px-3 py-1 rounded-full">0{i + 1}</div>
                </div>
                <div className="p-6">
                  <div className="font-display text-xl md:text-2xl text-primary font-semibold mb-2">{svc.title}</div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{svc.description}</p>
                  <span className="text-sm font-semibold text-accent inline-flex items-center gap-1 group-hover:gap-2 transition-all">Saiba Mais <ChevronRight className="w-4 h-4" /></span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </section>

      <section className="bg-secondary/40 py-14 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-50" />
        <div className="container-pad relative">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-accent" />
            <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">Especialidades</span>
          </div>
          <h2 className="font-display text-2xl md:text-4xl text-primary mb-3 text-balance font-light">Áreas de atuação</h2>
          <p className="text-muted-foreground mb-10 max-w-2xl text-sm">Atendimento completo para problemas relacionados ao ouvido, nariz, garganta, voz, alergias respiratórias, tonturas e medicina do sono.</p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Headphones, title: "Ouvido e Labirinto", desc: "Audiometria, BERA, exames otoneurológicos, tonturas, vertigem, zumbido e alterações auditivas." },
              { icon: Wind, title: "Nariz e Alergias", desc: "Videonasofibroscopia, rinite, sinusite, testes alérgicos e imunoterapia." },
              { icon: Stethoscope, title: "Garganta, Voz e Sono", desc: "Videolaringoscopia, videoestroboscopia, rouquidão, ronco e apneia do sono." },
            ].map((a, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-border/60 hover:border-accent/40 transition group">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-emerald-600 text-accent-foreground flex items-center justify-center mb-4 group-hover:scale-110 transition">
                  <a.icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg text-primary font-semibold mb-2">{a.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-pad py-14 md:py-16" data-testid="service-links-section">
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="w-8 h-px bg-accent" />
          <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">Exames & Procedimentos</span>
        </div>
        <h2 className="font-display text-2xl md:text-4xl text-primary mb-3 text-balance font-light">Lista de serviços</h2>
        <p className="text-muted-foreground mb-10 max-w-2xl text-sm">Confira os principais exames e tratamentos disponíveis.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {links.map((sl, i) => {
            const internal = (sl.link || "").startsWith("/");
            const cardClass = "group bg-white border border-border rounded-2xl p-5 hover:border-accent hover:shadow-lg transition flex items-center justify-between gap-3";
            const inner = (
              <>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-secondary to-secondary/40 text-primary group-hover:from-accent group-hover:to-accent/80 group-hover:text-white transition flex items-center justify-center font-display font-semibold text-sm shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display text-primary group-hover:text-accent transition font-semibold text-sm md:text-base truncate">{sl.title}</div>
                </div>
                {internal ? <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition shrink-0" /> : <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-accent transition shrink-0" />}
              </>
            );
            return internal ? (
              <motion.div
                key={sl.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 6) * 0.05 }}
                data-testid={`service-link-${i}`}
              >
                <Link to={sl.link} className={cardClass}>{inner}</Link>
              </motion.div>
            ) : (
              <motion.a
                key={sl.title}
                href={sl.link}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 6) * 0.05 }}
                className={cardClass}
                data-testid={`service-link-${i}`}
              >
                {inner}
              </motion.a>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Servicos;
